import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navigation.css';

function Navigation({ socket }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  // Chat states
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showAlbum, setShowAlbum] = useState(false);
  const [albumImages, setAlbumImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [mobileView, setMobileView] = useState('trading');
  const [adminChatUnread, setAdminChatUnread] = useState(0);

  // Premium Professional Icons as SVG Components
  const HomeIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ProfileIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const TradeIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 20L17 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 12L17 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 16L13 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17 20L7 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const WalletIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 7V11H17V7H5V5H17C18.105 5 19 5.895 19 7ZM3 19V9H21V19C21 20.105 20.105 21 19 21H5C3.895 21 3 20.105 3 19Z" stroke="currentColor" strokeWidth="2" fill="none"/>
      <path d="M15 13C15.5523 13 16 13.4477 16 14C16 14.5523 15.5523 15 15 15C14.4477 15 14 14.5523 14 14C14 13.4477 14.4477 13 15 13Z" fill="currentColor"/>
    </svg>
  );

  const MarketsIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 12L7 8L11 12L16 7L21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 17L7 13L11 17L16 12L21 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 22H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const KycIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 5H20V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M15 3V7H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChatIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const AdminIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M19.4 15C19.2663 15.3044 19.1779 15.6305 19.1387 15.9657C19.0995 16.3009 19.11 16.6406 19.1697 16.9722L19.4 18L18.0707 18.7272C17.8568 18.8488 17.6684 19.0118 17.5161 19.2068C17.3638 19.4018 17.2505 19.625 17.1826 19.8636L17 20.5L15.9747 20.3645C15.6488 20.3181 15.3165 20.3318 14.9954 20.4049C14.6743 20.478 14.3702 20.6091 14.0993 20.7914L13.2 21.4L12.5645 20.4355C12.3821 20.1646 12.1516 19.9311 11.8854 19.7476C11.6191 19.5641 11.3221 19.4341 11.0103 19.3645L9.5 19L9.3174 18.1364C9.24952 17.8978 9.13619 17.6746 8.98391 17.4796C8.83163 17.2846 8.64318 17.1216 8.4293 17L7.1 16.2728L7.3303 15.0278C7.39 14.6962 7.40053 14.3565 7.36133 14.0213C7.32213 13.6861 7.23374 13.36 7.1 13.0556L6.5 12L7.1 10.9444C7.23374 10.64 7.32213 10.3139 7.36133 9.97871C7.40053 9.6435 7.39 9.3038 7.3303 8.97222L7.1 7.72722L8.4293 7C8.64318 6.87842 8.83163 6.71538 8.98391 6.52038C9.13619 6.32538 9.24952 6.10218 9.3174 5.86364L9.5 5L11.0103 4.63546C11.3221 4.56594 11.6191 4.43588 11.8854 4.25241C12.1516 4.06894 12.3821 3.83538 12.5645 3.56454L13.2 2.6L14.0993 3.20857C14.3702 3.39094 14.6743 3.522 14.9954 3.59509C15.3165 3.66818 15.6488 3.68194 15.9747 3.63546L17 3.5L17.1826 4.13636C17.2505 4.3749 17.3638 4.5981 17.5161 4.7931C17.6684 4.9881 17.8568 5.15114 18.0707 5.27278L19.4 6L19.1697 7.2451C19.11 7.57668 19.0995 7.91638 19.1387 8.25159C19.1779 8.5868 19.2663 8.91289 19.4 9.21725L20 10.3333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const LogoutIcon = ({ className = "" }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Detect scroll and manage body class
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    // Load theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDarkTheme(savedTheme === 'dark');
      document.body.className = savedTheme === 'dark' ? 'dark-theme' : 'light-theme';
    }

    // Manage body class for nav open state
    if (isMobileMenuOpen) {
      document.body.classList.add('nav-open');
    } else {
      document.body.classList.remove('nav-open');
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('nav-open');
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!socket || !user || !['admin', 'moderator'].includes(user.role)) return;

    const joinAdmin = () => socket.emit('joinAdminRoom');
    joinAdmin();
    socket.on('connect', joinAdmin);
    socket.on('authenticated', joinAdmin);

    const loadUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://trcsite.net/api/chat/admin/unread-count', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAdminChatUnread(data.count || 0);
        }
      } catch (err) {
        console.warn('Failed to load unread chat count', err);
      }
    };

    loadUnread();

    const handleAdminChat = (message) => {
      if (message?.sender_role === 'user') {
        setAdminChatUnread((count) => count + 1);
      }
    };

    socket.on('adminChatMessage', handleAdminChat);

    return () => {
      socket.off('connect', joinAdmin);
      socket.off('authenticated', joinAdmin);
      socket.off('adminChatMessage', handleAdminChat);
    };
  }, [socket, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isChatOpen && user) {
      loadChatRoom();
    }
  }, [isChatOpen, user]);

  useEffect(() => {
    if (!socket || !room) return;

    console.log('Joining chat room:', room.id);
    socket.emit('joinChatRoom', room.id);

    const handleNewMessage = (message) => {
      console.log('New message received:', message);
      setMessages(prev => [...prev, message]);
      markMessagesAsRead();
    };

    socket.on('newMessage', handleNewMessage);

    return () => {
      console.log('Leaving chat room:', room.id);
      socket.emit('leaveChatRoom', room.id);
      socket.off('newMessage', handleNewMessage);
    };
  }, [socket, room]);

  // Auto-focus input when chat opens
  useEffect(() => {
    if (isChatOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isChatOpen, messages]);

  const loadChatRoom = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setError('No authentication token found');
        return;
      }

      console.log('Loading chat room...');
      const response = await fetch('https://trcsite.net/api/chat/room', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Chat room loaded:', data);
        setRoom(data.room);
        setMessages(data.messages);
        markMessagesAsRead();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load chat room');
        console.error('Failed to load chat room:', errorData);
      }
    } catch (error) {
      console.error('Error loading chat room:', error);
      setError('Failed to load chat room');
    } finally {
      setLoading(false);
    }
  };

  const loadAlbum = async () => {
    if (!room) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`https://trcsite.net/api/chat/room/${room.id}/album`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Album loaded:', data.images.length, 'images');
        setAlbumImages(data.images);
        setShowAlbum(true);
      } else {
        const errorData = await response.json();
        console.error('Failed to load album:', errorData);
        setError('Failed to load photo album');
      }
    } catch (error) {
      console.error('Error loading album:', error);
      setError('Failed to load photo album');
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async () => {
    if (!room) return;

    try {
      const token = localStorage.getItem('token');
      await fetch('https://trcsite.net/api/chat/messages/read', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ room_id: room.id })
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !room || sending) return;

    try {
      setSending(true);
      setError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setError('No authentication token found');
        return;
      }

      console.log('Sending message:', newMessage);
      const response = await fetch('https://trcsite.net/api/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          room_id: room.id,
          message: newMessage.trim()
        })
      });

      if (response.ok) {
        const sentMessage = await response.json();
        console.log('Message sent successfully:', sentMessage);
        setNewMessage('');
        // Auto-focus input after sending
        if (inputRef.current) {
          inputRef.current.focus();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to send message');
        console.error('Failed to send message:', errorData);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('File selected:', file.name, file.type, file.size);

    // Check if file is an image
    if (file.type.startsWith('image/')) {
      // Show preview for images
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage({
          file: file,
          preview: e.target.result
        });
      };
      reader.readAsDataURL(file);
    } else {
      // Upload non-image files directly
      uploadFile(file);
    }
  };

  const uploadFile = async (file, caption = '') => {
    if (!file || !room) return;

    try {
      setUploading(true);
      setError('');
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('room_id', room.id);
      formData.append('message', caption || `Shared ${file.type.startsWith('image/') ? 'image' : 'file'}: ${file.name}`);

      console.log('Uploading file:', file.name, 'to room:', room.id);

      const response = await fetch('https://trcsite.net/api/chat/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const sentMessage = await response.json();
        console.log('File uploaded successfully:', sentMessage);
        setPreviewImage(null);
        // Auto-focus input after upload
        if (inputRef.current) {
          inputRef.current.focus();
        }
      } else {
        const errorData = await response.json();
        console.error('Upload failed:', errorData);
        setError(errorData.error || 'Failed to upload file');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file: ' + error.message);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const sendImageWithCaption = async (caption) => {
    if (!previewImage) return;
    await uploadFile(previewImage.file, caption);
  };

  const cancelPreview = () => {
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    document.body.className = newTheme ? 'dark-theme' : 'light-theme';
  };

  // Handle navigation clicks
  const handleNavClick = () => {
    closeMobileMenu();
  };

  const handleLinkClick = (e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute('href');
    handleNavClick();
    // Use setTimeout to ensure menu closes before navigation
    setTimeout(() => {
      navigate(href);
    }, 100);
  };

  const handleLogout = () => {
    logout();
    closeMobileMenu();
    setIsChatOpen(false);
    navigate('/');
  };

  const handleChatToggle = () => {
    setIsChatOpen(!isChatOpen);
    closeMobileMenu();
  };

  // Check if link is active
  const isActiveLink = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const unreadCount = messages.filter(msg => !msg.is_read && msg.sender_id !== user?.id).length;

  // FIXED: Check if there are any image messages in the current room
  const hasImages = messages.some(msg =>
    msg.message_type === 'image' && msg.file_url
  );

  return (
    <>
      {/* Compact Navigation Button */}
      <div className={`compact-nav-container ${isDarkTheme ? 'dark-theme' : 'light-theme'} ${scrolled ? 'scrolled' : ''}`}>
        <button
          className={`compact-nav-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </div>

      {/* Expanded Navigation Menu */}
      <div className={`compact-nav-menu ${isMobileMenuOpen ? 'active' : ''} ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div
            className="compact-nav-overlay"
            onClick={closeMobileMenu}
            aria-hidden="true"
          ></div>
        )}

        {/* Navigation Content */}
        <div className="compact-nav-content">
          {/* Header */}
          <div className="compact-nav-header">
            <div className="compact-nav-brand">
              <div className="brand-logo">
                <img src="/logo.svg" alt="Logo" className="logo-image" />
                <span className="logo-text">CryptoPro</span>
              </div>
            </div>
            <button
              className="compact-nav-close-btn"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>

          {/* User Info */}
          {user && (
            <div className="compact-user-info">
              <div className="user-avatar">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.name || 'User'}</span>
                <span className="user-role">{user?.role || 'user'}</span>
              </div>
            </div>
          )}

          {/* Navigation Links with Premium Icons */}
          <ul className="compact-nav-links">
            <li className="nav-item">
              <Link
                to="/profile"
                className={`nav-link ${isActiveLink('/profile') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                <span className="nav-icon"><ProfileIcon /></span>
                <span className="nav-text">Profile</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/"
                className={`nav-link ${isActiveLink('/') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <span className="nav-icon"><HomeIcon /></span>
                <span className="nav-text">Home</span>
              </Link>
            </li>

            <li className="nav-item">
              <Link
                to="/markets"
                className={`nav-link ${isActiveLink('/markets') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                <span className="nav-icon"><MarketsIcon /></span>
                <span className="nav-text">Markets</span>
              </Link>
            </li>
            <li className="nav-item">
              {user && (
                <Link
                  to="/trade"
                  className={`nav-link ${isActiveLink('/trade') ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  <span className="nav-icon"><TradeIcon /></span>
                  <span className="nav-text">Trade</span>
                </Link>
              )}
            </li>

            <li className="nav-item">
            {user && (
              <Link
                to="/wallet"
                className={`nav-link ${isActiveLink('/wallet') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <span className="nav-icon"><WalletIcon /></span>
                <span className="nav-text">Wallet</span>
              </Link>
            )}
            </li>

            {user && (
              <>
                <li className="nav-item">
                  <Link
                    to="/kyc-verification"
                    className={`nav-link ${isActiveLink('/kyc-verification') ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    <span className="nav-icon"><KycIcon /></span>
                    <span className="nav-text">KYC</span>
                  </Link>
                </li>

                {/* Quick Support Button */}
                <li className="nav-item">
                  <button
                    className={`nav-link chat-support-btn ${isChatOpen ? 'active' : ''}`}
                    onClick={handleChatToggle}
                  >
                    <span className="nav-icon"><ChatIcon /></span>
                    <span className="nav-text">Quick Support</span>
                    {unreadCount > 0 && (
                      <span className="unread-badge">{unreadCount}</span>
                    )}
                  </button>
                </li>

                {/* Admin Section */}
                {user?.role === 'admin' && (
                  <>
                    <li className="nav-item">
                      <Link
                        to="/admin"
                        className={`nav-link admin-link ${isActiveLink('/admin') ? 'active' : ''}`}
                        onClick={handleNavClick}
                      >
                        <span className="nav-icon"><AdminIcon /></span>
                        <span className="nav-text">Admin Dashboard</span>
                      </Link>
                    </li>

                    <li className="nav-item">
                      <Link
                        to="/admin/chat"
                        className={`nav-link admin-link ${isActiveLink('/admin/chat') ? 'active' : ''}`}
                        onClick={() => {
                          handleNavClick();
                          setAdminChatUnread(0);
                        }}
                      >
                        <span className="nav-icon"><ChatIcon /></span>
                        <span className="nav-text">Manage Chats</span>
                        {adminChatUnread > 0 && (
                          <span className="nav-chat-badge">{adminChatUnread > 99 ? '99+' : adminChatUnread}</span>
                        )}
                      </Link>
                    </li>
                  </>
                )}
              </>
            )}
          </ul>

          {/* Navigation Controls */}
          <div className="compact-nav-controls">
            {/* Auth Section */}
            <div className="compact-auth-section">
              {user ? (
                <div className="user-actions">
                  <button
                    className="logout-btn"
                    onClick={handleLogout}
                  >
                    <span className="nav-icon"><LogoutIcon /></span>
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <div className="auth-buttons">
                  <Link
                    to="/login"
                    className="auth-btn login-btn"
                    onClick={handleNavClick}
                  >
                    <span className="auth-icon">🔐</span>
                    <span>Login</span>
                  </Link>
                  <Link
                    to="/register"
                    className="auth-btn register-btn"
                    onClick={handleNavClick}
                  >
                    <span className="auth-icon">✨</span>
                    <span>Sign Up</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Footer Navigation with Premium Icons */}
      <div className="mobile-footer-nav">
        <Link
          to="/"
          className={`footer-nav-item ${isActiveLink('/') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          <span className="footer-nav-icon"><HomeIcon /></span>
          <span className="footer-nav-text">Home</span>
        </Link>

        <Link
          to="/markets"
          className={`footer-nav-item ${isActiveLink('/markets') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          <span className="footer-nav-icon"><MarketsIcon /></span>
          <span className="footer-nav-text">Markets</span>
        </Link>

        {user && (
          <Link
            to="/trade"
            className={`footer-nav-item ${isActiveLink('/trade') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span className="footer-nav-icon"><TradeIcon /></span>
            <span className="footer-nav-text">Trade</span>
          </Link>
        )}

        {user && (
          <Link
            to="/wallet"
            className={`footer-nav-item ${isActiveLink('/wallet') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span className="footer-nav-icon"><WalletIcon /></span>
            <span className="footer-nav-text">Wallet</span>
          </Link>
        )}
      </div>

      {/* Quick Support Chat Box */}
      {isChatOpen && user && (
        <div className="nav-chat-box">
          <div className="nav-chat-header">
            <div className="chat-header-info">
              <h3><ChatIcon /> Quick Support</h3>
              <span className="chat-status-indicator online"></span>
            </div>
            <div className="chat-header-actions">
              {/* FIXED: Album button now properly checks for images */}
              {hasImages && (
                <button
                  className="album-btn"
                  onClick={loadAlbum}
                  title="View Photo Album"
                  disabled={loading}
                >
                  📸
                </button>
              )}
              <button
                className="nav-chat-close-btn"
                onClick={() => setIsChatOpen(false)}
                aria-label="Close chat"
              >
                ×
              </button>
            </div>
          </div>

          {error && (
            <div className="nav-chat-error">
              <span>{error}</span>
              <button onClick={() => setError('')} aria-label="Clear error">×</button>
            </div>
          )}

          {/* Image Preview Modal */}
          {previewImage && (
            <div className="nav-image-preview-modal">
              <div className="nav-image-preview-content">
                <div className="nav-preview-header">
                  <h4>Send Image</h4>
                  <button onClick={cancelPreview}>×</button>
                </div>
                <div className="nav-preview-image">
                  <img src={previewImage.preview} alt="Preview" />
                </div>
                <div className="nav-preview-actions">
                  <input
                    type="text"
                    placeholder="Add a caption (optional)"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        sendImageWithCaption(e.target.value);
                      }
                    }}
                    autoFocus
                  />
                  <div className="nav-preview-buttons">
                    <button onClick={cancelPreview} className="nav-btn-cancel">
                      Cancel
                    </button>
                    <button
                      onClick={() => sendImageWithCaption('')}
                      className="nav-btn-send"
                      disabled={uploading}
                    >
                      {uploading ? 'Sending...' : 'Send Image'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Photo Album Modal */}
          {showAlbum && (
            <div className="nav-album-modal">
              <div className="nav-album-modal-content">
                <div className="nav-album-header">
                  <h3>📸 Photo Album</h3>
                  <button onClick={() => setShowAlbum(false)}>×</button>
                </div>
                <div className="nav-album-grid">
                  {loading ? (
                    <div className="nav-album-loading">
                      <div className="loading-spinner"></div>
                      <span>Loading album...</span>
                    </div>
                  ) : albumImages.length === 0 ? (
                    <div className="nav-no-images">No images shared yet</div>
                  ) : (
                    albumImages.map((image) => (
                      <div
                        key={image.id}
                        className="nav-album-item"
                        onClick={() => setSelectedImage(image)}
                      >
                        <img
                          src={`https://trcsite.net${image.file_url}`}
                          alt={image.file_name}
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/150x150/667eea/ffffff?text=Image+Not+Found';
                          }}
                        />
                        <div className="nav-album-item-info">
                          <span className="nav-file-name">{image.file_name}</span>
                          <span className="nav-upload-date">
                            {new Date(image.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Image View Modal */}
          {selectedImage && (
            <div className="nav-image-modal">
              <div className="nav-image-modal-content">
                <div className="nav-image-modal-header">
                  <span>{selectedImage.file_name}</span>
                  <button onClick={() => setSelectedImage(null)}>×</button>
                </div>
                <div className="nav-image-modal-body">
                  <img
                    src={`https://trcsite.net${selectedImage.file_url}`}
                    alt={selectedImage.file_name}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300/667eea/ffffff?text=Image+Not+Found';
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="nav-chat-messages">
            {loading ? (
              <div className="nav-chat-loading">
                <div className="loading-spinner"></div>
                <span>Loading messages...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="nav-chat-no-messages">
                <div className="no-messages-icon"><ChatIcon /></div>
                <p>No messages yet</p>
                <span>Start a conversation with our support team!</span>
                <div className="nav-upload-hint">
                  <p>📎 Click the paperclip icon to upload photos and files</p>
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`nav-chat-message ${message.sender_id === user.id ? 'nav-own-message' : 'nav-other-message'}`}
                >
                  <div className="nav-message-header">
                    <span className="nav-sender-name">
                      {message.sender_id === user.id ? 'You' : message.sender_name}
                      {message.sender_role !== 'user' && ` (${message.sender_role})`}
                    </span>
                    <span className="nav-message-time">
                      {formatTime(message.created_at)}
                    </span>
                  </div>

                  {/* Image and File Messages */}
                  {message.message_type === 'image' && message.file_url ? (
                    <div className="nav-message-image">
                      <img
                        src={`https://trcsite.net${message.file_url}`}
                        alt={message.file_name || 'Shared image'}
                        onClick={() => setSelectedImage(message)}
                        onError={(e) => {
                          console.error('Failed to load image:', message.file_url);
                          e.target.src = 'https://via.placeholder.com/200x150/667eea/ffffff?text=Image+Not+Found';
                        }}
                      />
                      <div className="nav-image-caption">
                        {message.message && message.message !== `Shared image: ${message.file_name}` && (
                          <p>{message.message}</p>
                        )}
                        <span className="nav-file-info">
                          {message.file_name} • {formatFileSize(message.file_size || 0)}
                        </span>
                      </div>
                    </div>
                  ) : message.message_type === 'file' && message.file_url ? (
                    <div className="nav-message-file">
                      <div className="nav-file-icon">📎</div>
                      <div className="nav-file-info">
                        <a
                          href={`https://trcsite.net${message.file_url}`}
                          download={message.file_name}
                          className="nav-file-link"
                        >
                          {message.file_name}
                        </a>
                        <span className="nav-file-size">{formatFileSize(message.file_size || 0)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="nav-message-content">
                      {message.message}
                    </div>
                  )}

                  {!message.is_read && message.sender_id === user.id && (
                    <div className="nav-message-status">Sent</div>
                  )}
                  {message.is_read && message.sender_id === user.id && (
                    <div className="nav-message-status">Read</div>
                  )}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="nav-chat-form">
            <div className="nav-chat-input-container">
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                disabled={sending || loading || uploading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
                autoFocus
              />

              <div className="nav-action-buttons">
                {/* PHOTO UPLOAD BUTTON - ALWAYS VISIBLE */}
                <label className="nav-file-upload-btn" title="Upload photo or file">
                  📎
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                </label>

                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending || loading || uploading}
                  className={`nav-chat-send-btn ${sending ? 'sending' : ''}`}
                >
                  {sending ? (
                    <>
                      <div className="nav-sending-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    'Send'
                  )}
                </button>
              </div>
            </div>

            {uploading && (
              <div className="nav-upload-progress">
                <div className="nav-progress-bar">
                  <div className="nav-progress-fill"></div>
                </div>
                <span>Uploading file...</span>
              </div>
            )}
          </form>
        </div>
      )}
    </>
  );
}

export default Navigation;
